"use client";

import React, { useEffect, useMemo, useState } from "react";
import { AeraButton } from "@/app/components/ui/AeraButton";
import { AeraImage } from "@/app/components/ui/AeraImage";
import Modal from "@/app/components/ui/Modal";
import { MODAL_PANEL_ANIMATION_MS } from "@/app/components/ui/modalMotion";

// Simple icon component (Copied from AgentChatPanel to be self-contained)
const Icon = ({ name, size = 16 }: { name: string; size?: number }) => {
  const icons: Record<string, React.ReactNode> = {
    file: (
      <>
        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
        <polyline points="14 2 14 8 20 8" />
      </>
    ),
  };

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {icons[name] || icons["file"]}
    </svg>
  );
};

type PreviewFile =
  | File
  | { name: string; url: string; type: string; size?: number };

interface FilePreviewModalProps {
  file: PreviewFile | null;
  onClose: () => void;
  isEmbeddedInModal?: boolean;
}

function isBlobLike(value: unknown): value is Blob {
  if (!value || typeof value !== "object") return false;
  if (typeof Blob !== "undefined" && value instanceof Blob) return true;
  return typeof (value as Blob).arrayBuffer === "function";
}

function getPreviewMeta(activeFile: PreviewFile) {
  const name =
    typeof (activeFile as File).name === "string"
      ? (activeFile as File).name
      : "download";
  const type =
    typeof (activeFile as { type?: string }).type === "string"
      ? (activeFile as { type?: string }).type ?? ""
      : "";
  const size =
    typeof (activeFile as { size?: number }).size === "number"
      ? (activeFile as { size?: number }).size
      : undefined;
  const existingUrl = (activeFile as { url?: string }).url;
  return { name, type, size, existingUrl };
}

function resolvePreviewUrl(activeFile: PreviewFile) {
  const { existingUrl } = getPreviewMeta(activeFile);
  if (typeof existingUrl === "string" && existingUrl.length > 0) {
    return { fileUrl: existingUrl, shouldRevoke: false };
  }
  if (isBlobLike(activeFile)) {
    return {
      fileUrl: URL.createObjectURL(activeFile),
      shouldRevoke: true,
    };
  }
  return { fileUrl: null as string | null, shouldRevoke: false };
}

function PdfPreview({ fileUrl }: { fileUrl: string }) {
  return (
    <object
      data={fileUrl}
      type="application/pdf"
      className="w-full h-full bg-aera-white"
      aria-label="PDF Preview"
    >
      <div className="flex flex-col items-center justify-center h-full p-8 text-center text-aera-slate bg-aera-white">
        <div className="bg-gray-100 p-4 rounded-full mb-4">
          <Icon name="file" size={32} />
        </div>
        <h3 className="text-lg font-semibold text-aera-slate mb-2">
          PDF preview not available
        </h3>
        <p className="max-w-md mb-4 text-sm">
          Your browser blocked inline PDF rendering. You can open or download
          the file instead.
        </p>
        <div className="flex items-center gap-3">
          <AeraButton
            as="a"
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="secondary"
            size="sm"
          >
            Open PDF
          </AeraButton>
          <AeraButton
            as="a"
            href={fileUrl}
            download
            variant="outline"
            size="sm"
          >
            Download
          </AeraButton>
        </div>
      </div>
    </object>
  );
}

function TextPreview({
  fileUrl,
  fileName,
}: {
  fileUrl: string;
  fileName: string;
}) {
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setText(null);
    setError(null);

    (async () => {
      try {
        const res = await fetch(fileUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const t = await res.text();
        if (!cancelled) setText(t);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load text");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fileUrl]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center text-aera-slate bg-aera-white">
        <div className="bg-gray-100 p-4 rounded-full mb-4">
          <Icon name="file" size={32} />
        </div>
        <h3 className="text-lg font-semibold text-aera-slate mb-2">
          Cannot preview this text file
        </h3>
        <p className="max-w-md mb-4 text-sm">
          {error}. You can open or download the file instead.
        </p>
        <div className="flex items-center gap-3">
          <AeraButton
            as="a"
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="secondary"
            size="sm"
          >
            Open
          </AeraButton>
          <AeraButton
            as="a"
            href={fileUrl}
            download={fileName}
            variant="outline"
            size="sm"
          >
            Download
          </AeraButton>
        </div>
      </div>
    );
  }

  if (text === null) {
    return (
      <div className="w-full h-full bg-aera-white flex items-center justify-center">
        <div className="flex items-center gap-2 text-aera-gray text-sm">
          <div className="w-4 h-4 border-2 border-aera-lightgray border-t-transparent rounded-full animate-spin" />
          Loading…
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-aera-white overflow-auto">
      <pre className="p-4 text-xs leading-relaxed whitespace-pre-wrap wrap-break-word text-aera-charcoal">
        {text}
      </pre>
    </div>
  );
}

function OfficeDocPreview({
  fileUrl,
  fileName,
}: {
  fileUrl: string;
  fileName: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center h-full p-8 text-center text-aera-slate bg-aera-white">
      <div className="bg-gray-100 p-4 rounded-full mb-4">
        <Icon name="file" size={32} />
      </div>
      <h3 className="text-lg font-semibold text-aera-slate mb-2">
        Office preview not available
      </h3>
      <p className="max-w-md mb-4 text-sm">
        This preview previously used an embedded viewer. To avoid iframes,
        please open the file externally or download it.
      </p>
      <div className="flex items-center gap-3">
        <AeraButton
          as="a"
          href={fileUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="secondary"
          size="sm"
        >
          Open
        </AeraButton>
        <AeraButton
          as="a"
          href={fileUrl}
          download={fileName}
          variant="outline"
          size="sm"
        >
          Download
        </AeraButton>
      </div>
    </div>
  );
}

export default function FilePreviewModal({
  file,
  onClose,
  isEmbeddedInModal = false,
}: FilePreviewModalProps) {
  const [displayFile, setDisplayFile] =
    useState<FilePreviewModalProps["file"]>(null);

  useEffect(() => {
    if (file) {
      setDisplayFile(file);
      return;
    }
    const t = window.setTimeout(
      () => setDisplayFile(null),
      MODAL_PANEL_ANIMATION_MS + 80,
    );
    return () => window.clearTimeout(t);
  }, [file]);

  const activeFile = file ?? displayFile;

  const fileUrlInfo = useMemo(() => {
    if (!activeFile) return null;
    return resolvePreviewUrl(activeFile);
  }, [activeFile]);

  const fileUrl = fileUrlInfo?.fileUrl ?? null;

  useEffect(() => {
    if (!fileUrl || !fileUrlInfo?.shouldRevoke) return;
    return () => {
      URL.revokeObjectURL(fileUrl);
    };
  }, [fileUrl, fileUrlInfo?.shouldRevoke]);

  if (!displayFile || !activeFile || !fileUrl) return null;

  const { name: activeName, type: activeType, size: activeSize } =
    getPreviewMeta(activeFile);

  const previewBody = (
    <div
      className={`flex items-center justify-center overflow-hidden p-0 ${(() => {
        const isImage =
          activeType.startsWith("image/") ||
          ["jpg", "png", "jpeg", "gif", "webp", "svg", "bmp"].includes(
            activeType.toLowerCase(),
          );
        return isImage ? "h-auto" : "h-[80vh]";
      })()}`}
    >
      {(() => {
        let extension = "";
        if (activeName) {
          extension = activeName.split(".").pop()?.toLowerCase() || "";
        }

        if (!extension && activeType) {
          extension = activeType.includes("/")
            ? activeType.split("/")[1]?.toLowerCase() || ""
            : activeType.toLowerCase();
        }

        const isImage =
          activeType.startsWith("image/") ||
          ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(
            extension,
          );
        const isPdf = extension === "pdf";
        const isOfficeDoc = [
          "doc",
          "docx",
          "xls",
          "xlsx",
          "ppt",
          "pptx",
        ].includes(extension);
        const isTextDoc = ["txt", "csv", "json", "xml", "log", "md"].includes(
          extension,
        );

        if (isImage) {
          return (
            <AeraImage
              native
              src={fileUrl}
              alt="preview"
              width={800}
              height={600}
              className="max-w-full max-h-full object-contain"
            />
          );
        }

        if (isPdf) {
          return <PdfPreview fileUrl={fileUrl} />;
        }

        if (isTextDoc) {
          return <TextPreview fileUrl={fileUrl} fileName={activeName} />;
        }

        if (isOfficeDoc) {
          return <OfficeDocPreview fileUrl={fileUrl} fileName={activeName} />;
        }

        // Fallback: Do NOT use iframe for unknown types to avoid auto-download loops
        return (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center text-aera-slate">
            <div className="bg-gray-100 p-4 rounded-full mb-4">
              <Icon name="file" size={32} />
            </div>
            <h3 className="text-lg font-semibold text-aera-slate mb-2">
              No Preview Available
            </h3>
            <p className="max-w-md mb-4 text-sm">
              This file type ({extension || "unknown"}) cannot be previewed.
            </p>
            <AeraButton
              as="a"
              href={fileUrl}
              download={activeName}
              variant="secondary"
              size="sm"
            >
              Download File
            </AeraButton>
          </div>
        );
      })()}
    </div>
  );

  const fileTitle = (
    <div className="flex min-w-0 items-center gap-3">
      <div className="rounded-md border border-gray-100 bg-aera-white p-1.5 shadow-sm">
        <Icon name="file" size={16} />
      </div>
      <div className="min-w-0">
        <p className="truncate font-semibold text-aera-slate">{activeName}</p>
        <p className="text-xs text-aera-slate">
          {typeof activeSize === "number"
            ? `${(activeSize / 1024).toFixed(1)} KB`
            : activeType
              ? activeType.toUpperCase()
              : ""}
        </p>
      </div>
    </div>
  );

  const previewFooter = (
    <AeraButton
      type="button"
      variant="outline"
      fontWeight="medium"
      onClick={onClose}
    >
      ปิด
    </AeraButton>
  );

  if (isEmbeddedInModal) {
    return <>{previewBody}</>;
  }

  return (
    <Modal
      isOpen={Boolean(file)}
      onClose={onClose}
      title={fileTitle}
      headerClassName="border-gray-200 bg-gray-50"
      size="4xl"
      overlayClassName="bg-aera-black/80 backdrop-blur-sm"
      bodyClassName="p-0"
      footer={previewFooter}
    >
      {previewBody}
    </Modal>
  );
}
